/**
 * The parsing half of the markdown renderer, kept apart from the rendering so
 * it can be tested as plain functions.
 *
 * The two that matter are here: whether a link may be rendered at all, and
 * whether a line is a table. The first is a security check on text a model
 * wrote; the second is the difference between a table and a row of pipes.
 */

/**
 * The href to render, or null to render the label as plain text.
 *
 * An allow-list rather than a deny-list: `javascript:` and `data:` are the
 * ones anybody thinks of, and a list of what is permitted stays correct as
 * schemes get invented.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  if (href.startsWith('/') || href.startsWith('#')) return href;
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:' ? href : null;
  } catch {
    return null;
  }
}

/**
 * The in-app path for a link that points back at this dashboard, else null.
 *
 * `origin` is passed in rather than read from `window` so this is a function
 * of its arguments — the same reason it can be tested without a browser.
 */
export function internalPath(href: string, origin: string): string | null {
  if (href.startsWith('/')) return href;
  try {
    const u = new URL(href);
    return u.origin === origin ? `${u.pathname}${u.search}${u.hash}` : null;
  } catch {
    return null;
  }
}

export type InlineToken =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'code'; text: string }
  /** `href` is null when the target was refused; render the label as text. */
  | { kind: 'link'; label: string; href: string | null };

const INLINE = String.raw`(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|` + '`[^`]+`)';

/**
 * Splits a line into `[link](url)`, `**bold**`, `` `code` `` and plain text.
 *
 * The regex is constructed per call rather than shared. A `/g` regex carries
 * `lastIndex` between calls, and the renderer walks a link's label by calling
 * this again — with one shared regex the inner call rewinds the outer one and
 * the loop never ends. That hung the browser tab instead of rendering
 * something visibly wrong, so it is pinned below.
 */
export function inlineTokens(text: string): InlineToken[] {
  const re = new RegExp(INLINE, 'g');
  const out: InlineToken[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith('[')) {
      const cut = tok.indexOf('](');
      out.push({ kind: 'link', label: tok.slice(1, cut), href: safeHref(tok.slice(cut + 2, -1)) });
    } else if (tok.startsWith('**')) {
      out.push({ kind: 'bold', text: tok.slice(2, -2) });
    } else {
      out.push({ kind: 'code', text: tok.slice(1, -1) });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

/** Splits a pipe-table row, dropping the empty edges a leading `|` creates. */
export function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** The `|---|:--:|` line that turns the row above it into a header. */
export function isDivider(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
}

export function isRow(line: string): boolean {
  return line.trim().startsWith('|');
}

export interface Table {
  head: string[];
  body: string[][];
  /** Index of the first line after the table. */
  end: number;
}

/**
 * Reads a table starting at `i`, or null when there is not one there.
 *
 * A header row alone is just a line with pipes in it, so the divider on the
 * next line is what makes this a table at all.
 */
export function tableAt(lines: string[], i: number): Table | null {
  if (!isRow(lines[i] ?? '') || !isDivider(lines[i + 1] ?? '')) return null;
  const head = cells(lines[i]!);
  const body: string[][] = [];
  let j = i + 2;
  while (j < lines.length && isRow(lines[j]!)) {
    body.push(cells(lines[j]!));
    j++;
  }
  return { head, body, end: j };
}
